import { UUID } from "crypto";
import type { ReviewStatusFilter } from "./QueryTypes";
// AKL-731: `Image.boxes` is the shared render-ready box list every review
// surface (grid, film strip, full-screen) composes from — type-only import so
// this common module has no runtime dependency on the review feature.
import type { DetailBox } from "../review/mediaDetailComposer";

export type RoleAssignmentRequestDto = {
  role_id: number;
};

export type RoleAssignmentResponseDto = {
  message: string;
  user_id: UUID;
  role_id: number;
};

export type OrganizationPermissionDto = {
  name: string;
  value: number;
};

export type UserCreateDto = {
  first_name: string;
  last_name: string;
  email: string;
  organization?: string;
  job_title?: string;
  is_admin?: boolean;
};

export type Profile = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  organization: string;
  job_title: string;
  is_admin: false;
  is_verified: false;
  is_active: false;
  org_roles: any[];
  project_roles: any[];
  theme: "light" | "dark" | null;
};

export type OrganizationUserDto = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  organization: string;
  job_title: string;
  is_admin: false;
  is_verified: false;
  is_active: false;
  role_name: string;
  role_id: number;
};

export type OrganizationResponseDto = {
  org_id: number | undefined;
  org_name: string;
  guid: string;
  parent_organization_id: number | undefined;
  address: string | null;
  country_id: string;
  comments: string | null;
  website: string | null;
  primary_contact_email: string;
  primary_contact_phone: string | null;
  inherit_permissions: boolean;
};

export type OrganizationCreateDto = {
  org_name: string;
  parent_organization_id: number | undefined;
  address: string | null;
  country_id: string;
  comments: string | null;
  website: string | null;
  primary_contact_email: string;
  primary_contact_phone: string | null;
  inherit_permissions: boolean;
};

export type DropDownDto = {
  value: number;
  label: string;
};

export type UserInvitationCreate = {
  email: string;
  role_id: number;
};

export type DeploymentResponseDto = {
  deployment_id: number;
  deployment_name: string;
  guid: string;
  start_date: string;
  end_date: string;
  project_id: number;
  location_id?: number | null;
  location_name?: string | null;
  sensor_id: number | null;
  sensor_name: string | null;
  ml_ops_id?: number;
  schema_data?: JSON;
  media_asset_count: number;
};

export enum DeploymentStatus {
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  FAILED = "failed",
}

export type DeploymentEndStatusDto = {
  status: DeploymentStatus;
  file_count?: number;
};

export type DeploymentUploadStartDto = {
  file_count: number;
};

export type DeploymentUpdateDto = {
  deployment_name: string;
  deployment_id: number;
  start_date: string;
  end_date: string;
  project_id: number;
  location_id?: number | null;
  sensor_id: number | null;
  ml_ops_id?: number;
  guid?: string;
  // comments?: string | null;
  schema_data?: JSON;
};

export type DeploymentImportDto = {
  deployment_id: number;
  file_count: number;
};

export type DeploymentInferenceDto = {
  deployment_id: number;
  model_id?: number; // make not nullable once we remove legacy inference flow
  conf_thresh?: number;
  // AKL-766: "Run full pipeline" — queues a classification run (PENDING,
  // waiting on this detection run) alongside the detection request.
  pipeline_classification_model_id?: number;
  pipeline_classification_conf_thresh?: number;
};

export type ProjectCreateDto = {
  project_name: string;
  short_name: string;
  acronym: string | null;
  description: string | null;
  org_id: number | undefined;
  org_name: string;
  country_id: string;
  /**
   * IANA timezone identifier (e.g. "Africa/Nairobi"). Required by the API (AKL-722):
   * camera timestamps are stored timezone-naive as local camera time, so without this a
   * CamTrap DP export cannot produce the offset-bearing ISO 8601 the format requires.
   */
  timezone: string;
  inherit_permissions: boolean;
  use_default_storage: boolean;
  website: string | null;
  primary_contact_email: string;
  primary_contact_name: string;
  start_date: string | null;
  end_date: string | null;
  objectives: string;
  observation_level: string;
  species_focus: string;
  focal_species_id: string | null;
  sensor_layout: string;
  sensor_layout_target: string | null;
  sensors_clustered: boolean;
  capture_method: string;
  bait_used: string;
  bait_type: string | null;
  project_stratified: boolean;
  stratification_type: string | null;
  blank_images_removed: string;
  remove_images_containing_humans: boolean;
  coordinate_precision: number | null;
  photos_license: string;
  metadata_license: string;
  citation: string | null;
  comments: string | null;
  storage_url: string | null;
  container_name: string | null;
  schema_type?: string;
  deployment_schema: any;
  ui_schema: any;
};

export type ProjectResponseDto = {
  project_id: number;
  project_name: string;
  short_name: string;
  acronym: string | null;
  description: string | null;
  guid: string;
  org_id: number;
  org_name: string;
  country_id: string;
  /**
   * Nullable on the way out even though it is required on the way in: projects created
   * before AKL-722 genuinely hold null until someone saves their settings. Typing this
   * `string` would be a lie the form then trips over.
   */
  timezone: string | null;
  /**
   * Read-only provenance stamped by the API — "user" when a human chose the zone,
   * "derived" when it was inferred. Never set from the client; "derived" is what drives
   * CamTrap DP's timestampIssues disclosure flag.
   */
  timezone_source: string | null;
  inherit_permissions: boolean;
  use_default_storage: boolean;
  website: string | null;
  primary_contact_email: string;
  primary_contact_name: string;
  start_date: string | null;
  end_date: string | null;
  objectives: string;
  observation_level: string;
  species_focus: string;
  focal_species_id: string | null;
  sensor_layout: string;
  sensor_layout_target: string | null;
  sensors_clustered: boolean;
  capture_method: string;
  bait_used: string;
  bait_type: string | null;
  project_stratified: boolean;
  stratification_type: string | null;
  blank_images_removed: string;
  remove_images_containing_humans: boolean;
  coordinate_precision: number | null;
  photos_license: string;
  metadata_license: string;
  citation: string | null;
  comments: string | null;
  storage_url: string | null;
  container_name: string | null;
  deployment_schema: any;
  ui_schema: any;
};

export type LocationResponseDto = {
  location_id: number;
  location_name: string;
  guid: string;
  project_id: number;
  country_id: string;
  latitude: number;
  longitude: number;
};

export type LocationCreateUpdateDto = {
  location_name: string;
  location_id: number | undefined;
  project_id: number;
  latitude: number;
  longitude: number;
  country_id: string;
};

export type SensorCreateDto = {
  sensor_name: string;
  sensor_id: number | undefined;
  org_id: number;
  sensor_type: string;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_price: string | null;
  product_url: string | null;
  comments: string | null;
  is_active: boolean;
  decommission_reason: string | null;
};

export type SensorResponseDto = {
  sensor_id: number;
  sensor_name: string;
  guid: string;
  org_id: number;
  sensor_type: string;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_price: string | null;
  product_url: string | null;
  comments: string | null;
  is_active: boolean;
  decommission_reason: string | null;
};

export type DeploymentMetadataDto = {
  file_hashes: string[];
  deployment_id: number;
  project_id: number;
};

export type StartDeploymentResponse = {
  result: boolean;
  file_hashes?: string[];
};

export type Image = {
  id: string;
  url: string;
  name: string;
  projectGuid: string;
  deploymentGuid: string;
  deployment: string;
  location: string;
  sensor: string;
  captureTime: string | null;
  // AKL-731: composed once (via `composeDetailBoxes`) when the feed response is
  // converted to `Image[]` — the same render-ready box list (visibility-aware
  // color, resolved label, exclusion-fallback already applied) the full-screen
  // surface has always used. Replaces the old per-surface `Detection[]`
  // (`predictionComposer.tsx`, deleted) so the grid and film strip render
  // identically to full-screen for the same photo.
  boxes: DetailBox[];
  frameLabels?: FrameLabel[];
  // AKL-696: per-asset review status from the feed payload, used to flag
  // reviewed assets in the grid + filmstrip. Server truth (never inferred locally).
  review_status?: ReviewStatusFilter;
  last_reviewed_at?: string | null;
};

export type FrameLabel = {
  prediction_id: number;
  label: string;
  confidence: number | null;
};

export type OrgUserRole = {
  org_id: number;
  org_name: string;
  role: number;
  role_name: string;
};

export type ProjectUserRole = {
  project_id: number;
  project_name: string;
  role: number;
  role_name: string;
};

export type DeploymentTemplateResponseDto = {
  id: string; // Changed from number to string to accommodate UUIDs
  name: string;
  schema_definition: string;
  ui_schema?: string;
};

export type TaxonomyResponseDto = {
  authority: string | null;
  class_: string;
  common_name_english: string | null;
  created_at: string;
  family: string | null;
  genus: string | null;
  id: number;
  iucn_category_id: number | null;
  order: string | null;
  reference_url: string | null;
  scientific_name: string | null;
  species: string | null;
  subfamily: string | null;
  suborder: string | null;
  subspecies: string | null;
  superfamily: string | null;
  taxon_level: string;
  taxonomy_sub_type: string;
  taxonomy_type: string;
  unique_identifier: string;
  updated_at: string;
};

export type ModelResponseDto = {
  model_id: number;
  name: string;
  model_type: string;
  model_version: string;
  default_confidence_threshold: string;
  is_ann_format: boolean;
  input_has_bboxes: boolean;
};

// --- Project AI mappings & species list (AKL-679) ---------------------------
// These mirror the akili.admin.api project-admin DTOs 1:1. The project surface
// only ever deals with taxon and excluded mappings ("field_value" is rejected
// server-side), so the union is intentionally narrower than the DB enum.
export type TargetKind = "taxon" | "excluded";

export type ProjectMappingResponse = {
  id: number;
  ai_model_id: number;
  raw_label: string;
  target_kind: TargetKind;
  taxon_id?: number | null;
  display_name_override?: string | null;
  scope: string;
  project_id?: number | null;
};

export type EffectiveMappingItem = {
  id?: number | null; // null for a global-only fallback row
  ai_model_id: number;
  raw_label: string;
  target_kind: TargetKind;
  resolved_taxon_id?: number | null;
  resolved_display_name?: string | null;
  display_name_override?: string | null;
  source: "project" | "global";
  deletable: boolean;
};

export type EffectiveMappingsResponse = {
  mappings: EffectiveMappingItem[];
  total_count: number;
};

export type ProjectMappingCreateRequest = {
  ai_model_id: number;
  raw_label: string;
  target_kind?: TargetKind;
  taxon_id?: number | null;
  display_name_override?: string | null;
};

export type ProjectMappingUpdateRequest = {
  target_kind?: TargetKind;
  taxon_id?: number | null;
  display_name_override?: string | null;
};

// Bulk project-mapping upsert (AKL-751). One request applies the whole
// selection instead of one request per row. Each entry is an upsert keyed on
// `(project_id, ai_model_id, raw_label)`, which is why no `mapping_id` is
// sent — the client never decides create-vs-update. `display_name_override`
// is likewise server-side: a `taxon` repoint clears it, an `excluded` upsert
// leaves it untouched.
export type BulkMappingLabel = {
  ai_model_id: number;
  raw_label: string;
};

// Discriminated on `target_kind` so the API's own rule is enforced at compile
// time rather than discovered as a 422: a "taxon" repoint requires a
// `taxon_id`, an "excluded" upsert forbids one. The wire format is unchanged —
// this only rules out the two request shapes the API would have rejected.
export type BulkProjectMappingRequest =
  | {
      target_kind: "taxon";
      taxon_id: number;
      labels: BulkMappingLabel[];
    }
  | {
      target_kind: "excluded";
      /** Never sent for an exclusion — `never` makes passing one a type
       *  error, and an omitted key is absent from the JSON body. */
      taxon_id?: never;
      labels: BulkMappingLabel[];
    };

// Bulk reset to model default (AKL-751 round 2): the bulk counterpart of
// `resetProjectMapping`. Each entry deletes the label's project override so the
// model's global default stops being shadowed. No `target_kind` and no
// `taxon_id` — a reset removes a target rather than setting one. Reuses
// `BulkMappingLabel`, and the endpoint answers with `BulkProjectMappingResponse`
// below, so no new response type is needed.
export type BulkResetMappingsRequest = {
  labels: BulkMappingLabel[];
};

// `reason` is a stable machine-readable code, never prose — `model_not_enabled`
// and (bulk reset only) `no_global_default` are the ones defined today. Typed as
// `string` rather than a literal union because the API may add codes and the web
// side never branches on the value, so a narrower type would reject a valid
// response for no safety gain.
export type BulkMappingSkippedItem = {
  ai_model_id: number;
  raw_label: string;
  reason: string;
};

export type BulkProjectMappingResponse = {
  requested_count: number;
  applied_count: number;
  /** Rows already in the requested state — an idempotent no-op, not a
   *  failure, and never present in `skipped`. */
  unchanged_count: number;
  skipped: BulkMappingSkippedItem[];
};

// Bulk feed review actions (AKL-753). One request applies the whole selection —
// mark-blank, accept-suggestions or add-observation — instead of one request per
// photo, the same fan-out fix AKL-751 made for the mapping actions above. The
// response shape deliberately echoes `BulkProjectMappingResponse` so the two
// families of bulk action read alike; there is no `unchanged_count` because a
// review action has no "already in the requested state" case that isn't a skip.

// `reason` is a stable machine-readable code, never prose — `already_blank`,
// `nothing_to_accept` and `already_recorded` (AKL-811: a photo a reviewer had
// already recorded observations on, with nothing left to re-point) are the ones
// defined today. Typed as `string` rather than a literal union for the same
// reason `BulkMappingSkippedItem` is: the API may add codes, and the web maps
// them to copy rather than branching on them.
export type BulkReviewSkippedMedia = {
  media_id: number;
  reason: string;
};

export type BulkReviewActionResponse = {
  requested_count: number;
  applied_count: number;
  /** Photos the action had nothing to do for. Not failures — the batch still
   *  committed. */
  skipped: BulkReviewSkippedMedia[];
  /** True when the idempotency key had already been claimed: the batch committed
   *  on a prior attempt, so `applied_count` is 0 because *this* request wrote
   *  nothing. Without the flag that is indistinguishable from a no-op. */
  replayed: boolean;
};

export type BulkCreateObservationRequest = {
  media_ids: number[];
  taxon_id: number;
  individual_count: number | null;
  comments: string | null;
  /** Required, never defaulted. Mark-as-blank and accept-suggestions always set
   *  Reviewed; this one the reviewer can decline, via a checkbox that starts
   *  ticked. Required on the wire regardless of what that checkbox defaults to,
   *  so the answer is always stated rather than inferred from a server default
   *  that could drift from the UI. */
  set_reviewed: boolean;
  // No `idempotency_key`: the API wrapper mints one per call, exactly as the
  // other two bulk actions do. Declaring it here would promise callers a control
  // they do not have — and offering caller-supplied keys on one of the three,
  // when nothing retries a bulk action today, would just make them inconsistent.
};

/**
 * Point every selected photo at one species (AKL-811). Sits beside
 * `BulkCreateObservationRequest` on purpose: the two are nearly the same shape,
 * and the one difference between them is easier to see than to remember.
 */
export type BulkPointToSpeciesRequest = {
  media_ids: number[];
  taxon_id: number;
  // No `individual_count`, DELIBERATELY — a deliberate divergence from
  // `BulkCreateObservationRequest` that must not be "made consistent" with it
  // later. A count is a statement about one photo; one number written across an
  // arbitrary selection would assert something the reviewer never looked at. The
  // server derives it per photo from the number of DISTINCT BOUNDING BOXES the
  // re-pointed predictions cover — boxes, not predictions, because two
  // classifiers on one box are two predictions for one animal (AKL-775/776/777)
  // — and uses 1 for a photo the AI found nothing in.
  comments: string | null;
  /** Required, never defaulted — exactly as on `BulkCreateObservationRequest`.
   *  The checkbox's answer is always stated on the wire, so the UI and a server
   *  default can never drift apart. */
  set_reviewed: boolean;
  // No `idempotency_key`: the wrapper mints one per call, as all three AKL-753
  // siblings do. Declaring it here would promise callers a control they do not
  // have.
};

export type EffectiveSpeciesListItem = {
  taxon_id: number;
  display_name?: string | null;
  scientific_name?: string | null;
  sources: string[];
  label_override?: string | null;
  species_list_item_id?: number | null; // null when present only via mapping
};

export type EffectiveSpeciesListResponse = {
  items: EffectiveSpeciesListItem[];
  total_count: number;
};

// --- Project model enablement (AKL-729) -------------------------------------
// A model a project has explicitly enabled (auto-enabled on first run, or
// manually via `enableModelForProject`). `status` mirrors the model's live
// global status ("active"/"inactive" etc.) at read time — an enabled model
// keeps appearing (with a badge) even after it goes globally inactive.
// `disabled_at` is non-null when the project has disabled the model
// (`disableModelForProject`) — reversible: re-enabling (manually, or by
// re-running the model) clears it. Unlike `status`, this DOES gate whether
// the model's predictions surface (see `resolveEnablementStatus`).
export type EnabledModelItem = {
  model_id: number;
  name: string;
  model_type: string;
  model_version: string;
  status: string;
  enabled_at: string;
  disabled_at: string | null;
};

export type EnabledModelsResponse = {
  models: EnabledModelItem[];
};

// --- AI mappings label-first view model (AKL-715) --------------------------
// Client-side only — merges an EffectiveMappingItem (label-backed) or an
// EffectiveSpeciesListItem with no matching label (manual-only) into one row
// for the label-first table. Never sent to/from the API.
export type AiMappingsRow = {
  /** Stable id for React keys and the selection set. */
  rowId: string;
  taxon_id: number | null;
  raw_label: string | null; // null for a manual-only (added by hand) row
  resolved_display_name: string | null;
  display_name_override: string | null;
  scientific_name: string | null;
  target_kind: TargetKind | null; // null for a manual-only row with no mapping
  source: "project" | "global" | "manual";
  manual: boolean;
  deletable: boolean;
  canReset: boolean;
  mapping_id: number | null;
  ai_model_id: number | null;
  species_list_item_id: number | null;
  sources: string[]; // from EffectiveSpeciesListItem, e.g. "mapped via X"
  customized: boolean; // display_name_override differs from the resolved default
  matchedField?: "raw_label" | "species" | "display_name";
};

export type SpeciesListItemCreateRequest = {
  taxon_id: number;
  label?: string | null;
};

export type SpeciesListItemResponse = {
  id: number;
  species_list_id: number;
  taxon_id: number;
  label?: string | null;
};

export enum EventHistoryType {
  Upload = "Upload",
  Import = "Import",
  Detect = "Detect",
  Classify = "Classify",
}

export enum EventHistoryStatus {
  Completed = "Completed",
  Failed = "Failed",
  Cancelled = "Cancelled",
  InProgress = "In Progress",
}

export type EventHistoryItem = {
  type: EventHistoryType;
  status: EventHistoryStatus;
  file_count?: number;
  user_name: string;
  started_at: number | null;
  ended_at: number | null;
};

export type DeploymentEventSummary = {
  type: EventHistoryType;
  status: EventHistoryStatus;
  file_count?: number;
  /** The run owner's user id, as the summary document stores it (AKL-897 reads it). */
  user_id: string;
  user_name: string;
  ts: number;
};

export type Frame = {
  id: string;
  blob_path: string;
  name: string;
  detections: Detection[];
  timestamp: number;
  source_url: string;
  project_guid: string;
  deployment_guid: string;
  deployment_name?: string;
  location_name?: string;
  sensor_name?: string;
};

export type Detection = {
  confidence: number;
  height: number;
  id: string;
  label: string;
  width: number;
  x: number;
  y: number;
  metadata: Record<string, any>;
};

export type FramesDTO = {
  frames: Frame[];
};

export type GetFramesDto = {
  project_id: number;
  deployment_ids: number[];
  limit: number | null;
  skip: number;
};

export type SearchForImageDto = {
  project_id: number;
  deployment_ids?: number[];
  image_name: string;
  page_size: number;
};

export type ImageSearchResultDto = FramesDTO & {
  page: number;
  image_found: boolean;
  image_index: number | null;
  total: number;
};

export type PaginatedPredictionRunResponse = {
  items: PredictionRunItem[];
  total: number;
};

export type PredictionRunItem = {
  id: number;
  ai_model_id: number;
  model_name?: string;
  model_type?: string;
  deployment_id: number;
  deployment_name?: string;
  file_count?: number;
  initiated_by_user_id?: string;
  initiated_by_user_name?: string;
  clearml_task_id?: string;
  status: string;
  // AKL-766: set on a "Run full pipeline" classification run — the detection
  // run it is (or was) waiting on. Only meaningful while status is "pending".
  waiting_on_run_id?: number;
  created_at: string;
  updated_at: string;
  run_parameters_json?: Record<string, any>;
};

export type BatchInferenceDto = {
  deployment_ids: number[];
  model_id: number;
  conf_thresh?: number;
  bbs?: boolean | null;
  // AKL-766: see DeploymentInferenceDto — same pipeline chaining, applied
  // per-deployment across the batch.
  pipeline_classification_model_id?: number;
  pipeline_classification_conf_thresh?: number;
};

export type BatchInferenceResult = {
  deployment_id: number;
  deployment_name: string;
  ml_ops_id?: string;
  task_id?: string;
  status: "accepted" | "failed" | "skipped";
  error_message?: string;
};

export type BatchInferenceResponse = {
  results: BatchInferenceResult[];
  summary: {
    total: number;
    accepted: number;
    failed: number;
    skipped: number;
  };
};

export type BoundingBoxDto = {
  x: number;
  y: number;
  width: number;
  height: number;
};

// AKL-731: the feed/query endpoint (`/media-assets/query`, `/media-assets/search`)
// now returns the exact same prediction/suggestion/observation DTO family as the
// detail endpoint (`MediaDetailPrediction` etc., below) — one compose pipeline
// (`composeDetailBoxes`) serves the grid, film strip, and full-screen alike, so
// whatever is hidden/fallback-resolved server-side agrees across all three. The
// standalone `PredictionDto`/`PredictionLabelDto`/`ResolvedLabelDto` feed-only
// shapes (and the client-side override-walk that read them, `predictionComposer.tsx`)
// are retired in favor of this shared family.
export type MediaAssetDto = {
  media_asset_id: number;
  deployment_id: number;
  file_uri: string;
  file_name: string;
  capture_time_actual: string | null;
  predictions?: MediaDetailPrediction[];
  suggestions?: MediaDetailSuggestion[];
  observations?: MediaDetailObservation[];
  // AKL-696: per-asset review status (= server `annotation_status`), matching
  // MediaDetailResponseDto.review_status. Optional so a pre-deploy API degrades
  // gracefully to "unreviewed" rendering.
  review_status?: ReviewStatusFilter;
  last_reviewed_at?: string | null;
};

export type MediaAssetListDto = {
  media_assets: MediaAssetDto[];
  total: number;
};

export type MediaAssetSearchDto = MediaAssetListDto & {
  image_found: boolean;
  image_index: number | null;
  page: number;
};

// Response for POST /media-assets/query (AKL-680). Extends the list response
// with a server-computed counts payload. Field names and facet keys are
// intended to match the API contract exactly (see AKL-680).
export type MediaQueryCounts = {
  total: number;
  scope_total: number;
  // "The AI has finished looking at this photo at least once" (AKL-755).
  // Deliberately NOT derivable from `by_inference_state`: a photo a live run
  // has picked up reads `in_progress` there while still counting as processed
  // here, so no sum of those four mutually-exclusive buckets can produce this
  // figure. The web summed `completed + failed` until AKL-755, which is why the
  // AI Processed tile collapsed to 0 exactly while a run was going.
  ai_processed: number;
  by_review_status: { unreviewed: number; reviewed: number };
  by_observation_type: {
    animal: number;
    human: number;
    vehicle: number;
    blank: number;
    unknown: number;
  };
  by_deployment: Record<number, number>;
  by_inference_state: {
    no_run: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
};

export type MediaAssetQueryDto = MediaAssetListDto & {
  counts: MediaQueryCounts;
};

// --- Media detail endpoint (AKL-681, Photo Review Phase 2 Slice 5) ---------
// Response shape for POST /media-assets/detail. Field names are snake_case to
// mirror the API `MediaDetailResponse` verbatim (see the API-side spec
// akili.admin.api/_specs/AKL-681-photo-review-phase2-slice5.md). Resolution,
// visibility, and card aggregation are all computed server-side — the web
// never re-derives them.
// AKL-731: this same `MediaDetailPrediction`/`MediaDetailSuggestion`/
// `MediaDetailObservation`/`MediaDetailGeometry` family is now also what
// `MediaAssetDto.predictions`/`suggestions`/`observations` (the feed/query and
// search endpoints, above) carry per asset — one DTO family, two endpoints.

/** Observation type classification (mirrors the API observation_type enum). */
export type ObservationTypeValue =
  | "animal"
  | "human"
  | "vehicle"
  | "blank"
  | "unknown";

/** Overlay disposition for a prediction (§6.2.2 / §4.2). */
export type PredictionVisibility = "visible" | "out_of_list" | "suppressed";

/**
 * Overlay disposition for an aggregated suggestion card.
 * - `visible` — in-list card, pending a decision
 * - `out_of_list` — resolved but outside the project species list (overlay-only)
 * - `rejected` — a cluster the reviewer rejected (AKL-703). The API aggregates
 *   these separately from pending cards so a rejection, which writes review rows
 *   and no observation, still has somewhere to surface.
 * - `unresolved` — a still-pending box whose every label the project excluded
 *   and for which no AKL-731 fallback resolved (AKL-717). Not a card: it is the
 *   box alone, drawn unlabelled so the reviewer can assign a species to it or
 *   reject it. Its `target_taxon_id`, `display_name` and `observation_type`
 *   are null, it carries exactly one geometry, and object actions target
 *   `underlying_prediction_ids[0]` — the detection that owns the box.
 */
export type SuggestionVisibility =
  | "visible"
  | "out_of_list"
  | "rejected"
  | "unresolved";

/** Latest review state for a prediction (append-only trail head). */
export type PredictionReviewStatus = "accepted" | "rejected" | "edited";

/** Source of an existing observation. */
export type ObservationSource =
  | "human"
  | "ai_approved"
  | "ai_edited"
  | "import";

export type ObservationStatusValue = "draft" | "verified" | "deleted";

export type ObservationAppliesTo = "object" | "media";

export type DeploymentRef = {
  id: number;
  name: string;
  guid: string | null;
};

export type LocationRef = {
  id: number;
  name: string;
  latitude: number | null;
  longitude: number | null;
};

export type SensorRef = {
  id: number;
  name: string;
  make: string | null;
  model: string | null;
};

export type AIModelRef = {
  id: number;
  name: string;
  version: string | null;
};

export type LatestReviewRef = {
  id: number;
  status: PredictionReviewStatus;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  edits_json: Record<string, unknown> | null;
};

/**
 * Whether the reviewer may commit a candidate label, and if not, why (AKL-777).
 *
 * `selectable` is exactly `disposition === "visible"`. There is deliberately no
 * separate boolean saying so — two fields that must agree are two fields that
 * can drift, and one field cannot contradict itself.
 *
 * - `visible` — resolved, observable, in the project species list. Pickable.
 * - `out_of_list` — resolved but off the project's roster. Shown, not pickable
 *   (AKL-781: off-roster species are never *offered*, so the popover cannot
 *   become a back door around the species list).
 * - `unmapped` — no mapping at any scope. It does resolve, to Wildlife
 *   Insights Unknown, so it is technically committable — but committing
 *   "Unknown" from a specific raw label throws information away. Shown with the
 *   raw label, not pickable.
 * - `suppressed` — excluded / field value / non-observable. **Hidden entirely.**
 */
export type CandidateDisposition =
  | "visible"
  | "out_of_list"
  | "unmapped"
  | "suppressed";

export type MediaDetailPredictionLabel = {
  raw_label: string;
  score: number;
  rank: number | null;
  is_primary: boolean;
  /** AKL-777: populated on EVERY candidate. Was primary-only under §4.2, which
   * is why the popover fell back to raw model strings for every row but the
   * first. Still null for a candidate whose disposition is `suppressed`. */
  resolved_taxon_id?: number | null;
  resolved_display_name?: string | null;
  observation_type?: ObservationTypeValue | null;
  /** AKL-777. Optional so an API predating it degrades gracefully — and an
   * absent value must be treated as **not selectable**: an older API cannot
   * tell us an excluded alternative is excluded, and defaulting to selectable
   * would reintroduce the exact correctness bug this ticket closes (a reviewer
   * committing an observation to a taxon the project excluded). Fail closed. */
  disposition?: CandidateDisposition;
};

export type MediaDetailPrediction = {
  prediction_id: number;
  prediction_type: "detection" | "classification";
  applies_to: "media" | "object";
  parent_prediction_id: number | null;
  object_score: number | null;
  ai_model: AIModelRef | null;
  labels: MediaDetailPredictionLabel[];
  bbox: BoundingBoxDto | null;
  latest_review: LatestReviewRef | null;
  visibility: PredictionVisibility;
};

/**
 * One bounding box a suggestion or observation is composed from (AKL-703). The
 * API resolves these server-side, so the overlay renders straight from the item
 * that owns them instead of walking `predictions` to rebuild the linkage.
 *
 * `prediction_id` is the prediction that OWNS the box — always the object-level
 * detection — and is the stable box identity (`DetailBox.id` is its string form).
 * `label_prediction_id` is the prediction whose resolved label the box shows: the
 * object-level crop classification when one overrode the detection, else the same
 * detection. The prediction popover anchors to it.
 */
export type MediaDetailGeometry = {
  prediction_id: number;
  label_prediction_id: number;
  /** AKL-777: EVERY label-bearing prediction on this box, so a second
   * classifier's answer is reachable from the image. `label_prediction_id`
   * above stays — it still names the prediction whose label the box *shows*,
   * and the overlay's label resolution reads it. Optional so an API predating
   * AKL-777 degrades to the single-prediction popover it has today. */
  label_prediction_ids?: number[];
  geometry_id: number;
  bbox: BoundingBoxDto;
};

/**
 * The fields every suggestion carries, whatever its visibility. Split from the
 * species fields (AKL-717) so that an `unresolved` box — which has no species —
 * can be typed honestly as having none, while card code keeps a non-null taxon
 * without checking for one.
 */
type MediaDetailSuggestionBase = {
  aggregation_key: string;
  /** Scientific (Latin) name, shown in italics next to display_name; null when
   * the taxon has no scientific name or the API predates AKL-698. */
  scientific_name?: string | null;
  count: number;
  confidence_max: number;
  confidence_mean: number;
  underlying_prediction_ids: number[];
  /** Every model that contributed a prediction to this card (AKL-775), ordered
   * by model id. Named whether or not they agreed — precedence decides which
   * label is committed, never which models are shown. Optional so an API
   * predating AKL-775 degrades to "no provenance line" rather than an empty
   * label; empty for a legacy prediction whose run has no resolvable model. */
  models?: AIModelRef[];
  /** True only when every individual on this card was called by ALL of `models`
   * AND none of them was contested (AKL-783). `false` therefore covers two
   * different situations — the models contributed unevenly across the card, or
   * they contributed to everything but disagreed somewhere — and both are
   * honestly described by "Models: …" rather than "Models agree: …". Do not
   * collapse the two, and do not re-derive agreement client-side. */
  models_agree?: boolean;
  /** The cluster's bounding boxes (AKL-703). Optional so an API predating
   * AKL-703 degrades to "no boxes" rather than crashing the overlay. Always
   * empty for a `rejected` cluster — see `rejected_geometries` below. */
  geometries?: MediaDetailGeometry[];
  /** A `rejected` cluster's bounding boxes, held separately from `geometries`
   * (AKL-731 reject-reveal follow-up) so the default composition/render path
   * stays hidden-by-default while the full-screen panel can still reveal the
   * box on demand when its card is selected/hovered. Empty/absent for any
   * non-rejected cluster. Optional for the same predates-this-change reason
   * as `geometries`. */
  rejected_geometries?: MediaDetailGeometry[];
};

/**
 * A suggestion that resolved to a species: an in-list card, an out-of-list
 * overlay, or a rejected cluster. Every card the panel renders is one of these.
 */
export type ResolvedSuggestion = MediaDetailSuggestionBase & {
  visibility: "visible" | "out_of_list" | "rejected";
  target_taxon_id: number;
  display_name: string;
  observation_type: ObservationTypeValue;
};

/**
 * An `unresolved` box (AKL-717): the detection is real but its only label was
 * excluded by the project, so there is no species to name — and, deliberately,
 * no raw label either. Never a card; drawn unlabelled on the image.
 */
export type UnresolvedSuggestion = MediaDetailSuggestionBase & {
  visibility: "unresolved";
  target_taxon_id: null;
  display_name: null;
  /** Narrowed from the base's optional string: an unresolved box has no
   * species, so it cannot carry a scientific name either (Copilot, web#775). */
  scientific_name: null;
  observation_type: null;
};

/**
 * One entry of the detail payload's `suggestions[]`, discriminated on
 * `visibility`. Narrow with `isResolvedSuggestion` (mediaDetailComposer) before
 * reading the species fields.
 */
export type MediaDetailSuggestion = ResolvedSuggestion | UnresolvedSuggestion;

export type MediaDetailObservation = {
  id: number;
  taxon_id: number | null;
  display_name: string | null;
  /** Scientific (Latin) name, shown in italics next to display_name; null when
   * the taxon has no scientific name or the API predates AKL-698. */
  scientific_name?: string | null;
  individual_count: number | null;
  comments: string | null;
  observation_type: ObservationTypeValue;
  applies_to: ObservationAppliesTo;
  geometry_id: number | null;
  source: ObservationSource;
  source_prediction_id: number | null;
  status: ObservationStatusValue;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  /**
   * Every bounding box this observation was committed from (AKL-703) — one for an
   * object-scope accept, N for an aggregate accept over N detections. Resolved by
   * the API from the observation's own provenance, so it holds even once those
   * predictions are reviewed. Empty for observations with no box (manual
   * observations, blank markers). Optional so an API predating AKL-703 degrades to
   * "no boxes". `geometry_id` / `source_prediction_id` above are no longer the
   * linkage path.
   */
  geometries?: MediaDetailGeometry[];
};

export type MediaDetailNavigation = {
  prev_media_id: number | null;
  next_media_id: number | null;
  position: number | null; // 1-based; null when the media is not in the filter
  total_in_filter: number;
};

export type MediaDetailResponseDto = {
  media_asset_id: number;
  file_name: string;
  file_uri: string;
  capture_time_exif: string | null;
  capture_time_actual: string | null;
  review_status: string;
  exif_status: string;
  deployment: DeploymentRef;
  location: LocationRef | null;
  sensor: SensorRef | null;
  last_reviewed_at: string | null;
  last_reviewed_by_user_id: string | null;
  predictions: MediaDetailPrediction[];
  suggestions: MediaDetailSuggestion[];
  observations: MediaDetailObservation[];
  navigation: MediaDetailNavigation | null;
};

/** One prediction touched by a review action, with its new latest-review head. */
export type AffectedPrediction = {
  prediction_id: number;
  latest_review: LatestReviewRef | null;
};

/**
 * Partial post-action state returned by the `/review/*` write endpoints
 * (AKL-682). It is NOT the full media detail — the web re-fetches
 * `getMediaDetail` after each action to refresh predictions/suggestions/boxes
 * from server truth; this type documents the ack shape.
 */
export type ReviewActionResponse = {
  media_id: number;
  review_status: string;
  last_reviewed_at: string | null;
  last_reviewed_by_user_id: string | null;
  observations: MediaDetailObservation[];
  affected_predictions: AffectedPrediction[];
};
